import Widget from "./base.js";
import { postSubscribeUnlock } from 'tpm-lib/post-unlock.js';

// Shared, reusable newsletter signup form. Renders only the
// .NewsletterSignupForm__Form behavior (email entry + POST to
// doesspress/email-list) plus confirmation/error states. No description, no
// table of contents, no signed-in controls, no collapse chrome. Reuses the
// NewsletterSignupForm__* class names so it inherits the existing form styles
// (csstyle emits flat BEM selectors, so they apply regardless of parent).
//
// Self-contained: takes a `category` prop, does its own GET to
// doesspress/email-list, resolves list.id via categoryToList, and renders.
// Designed to be composed as a child — e.g. inside NewsletterSignupBlock in
// the archive sidebar, or any future component that needs a signup form.

// Normalize slugs for comparison: the legacy Brevo lists use underscores
// (the_backchannel) while editorial-newsletter rows use hyphens
// (the-backchannel). Match either convention so the widget works pre- and
// post-beehiiv-switchover.
const slugMatch = (a, b) => (a ?? "").replace(/[-_]/g, "-") === (b ?? "").replace(/[-_]/g, "-");

export class NewsletterSignupForm extends Widget {
	componentWillMount() {
		this.handleEmail = this.handleEmail.bind(this);
		this.getData();
	}

	componentDidMount() {
		const urlParams = new URLSearchParams(window.location.search);
		const addToMultipleLists = urlParams.get("addToMultipleLists");
		if (addToMultipleLists) {
			this.postData({
				addToMultipleLists,
				nextPath: encodeURIComponent(window.location.pathname),
			});
		}
	}

	getData() {
		this.request("GET");
	}

	postData(opts) {
		this.request("POST", opts);
	}

	request(method, data = {}) {
		const self = this;

		let params = `?`;
		Object.keys(data).forEach((k) => {
			params += `${k}=${data[k]}&`;
		});

		fetch(
			`https://accounts.${document.location.hostname}/doesspress/email-list${params}`,
			{
				method: `${method}`,
				credentials: "include",
			}
		)
			.then((r) => {
				return r.json();
			})
			.then((r) => {
				if (method === "GET") {
					self.setState({
						emailLists: r.bindings.emailLists,
						ident: r.bindings.ident,
					});
				} else {
					if (r.errors) {
						self.setState({ error: r.errors[0] });
					}
					if (r.confirmation) {
						// Post-subscribe unlock: shared transition (in-place content
						// swap when a token was minted; reload fallback otherwise).
						postSubscribeUnlock(r.confirmation, data);
						self.setState({ confirmation: r.confirmation });
						// opt-in hook for parents (e.g. EmailWall treats a
						// subscription as dismissal)
						if (typeof self.props.onConfirmation === "function")
							self.props.onConfirmation(r.confirmation);
					}
				}
			});
	}

	handleEmail(e) {
		e.preventDefault();

		const root = this.refs.base;
		const checkedInputs = root.querySelectorAll(
			".js_newsletter-checkbox:checked"
		);
		if (checkedInputs.length === 0) {
			this.setState({
				error: { message: "Please select a newsletter to subscribe to" },
			});
			return;
		} else {
			this.setState({
				error: null,
			});
		}

		const listIds = Array.from(checkedInputs).map(
			() => root.querySelector('input[name="listId"]').value
		);

		const opts = {
			addToMultipleLists: listIds.join(","),
			email: root.querySelector("#newEmail").value,
			nextPath: encodeURIComponent(window.location.pathname),
		};

		this.postData(opts);
	}

	categoryToList(category) {
		const lists = this.state.emailLists;

		if (category === "the-weekender") {
			return lists.find((list) => slugMatch(list.slug, "the_weekender"));
		}

		if (category === "the-franchise") {
			return lists.find((list) => slugMatch(list.slug, "the_franchise"));
		}

		if (category === "the-brief") {
			return lists.find((list) => slugMatch(list.slug, "the_brief"));
		}

		if (category === "the-backchannel") {
			return lists.find((list) => slugMatch(list.slug, "the_backchannel"));
		}

		if (category === "morning-memo") {
			return lists.find((list) => slugMatch(list.slug, "morning_memo"));
		}
		return null;
	}

	buttonClasses(){
		if (this.props.cssclasses.includes('--dark')) return 'Button --beige-to-black';
		if (this.props.cssclasses.includes('--red')) return 'Button --red-to-transparent';
		return 'Button --transparent-to-red';
	}

	renderForm(list) {
		const confirmAcctCreation = this.state.confirmation;

		return (
			<div>
				{!confirmAcctCreation && (
					<div className="NewsletterSignupForm__Form">
						<input type="hidden" name="listId" value={list.id} />
						<input
							className=" js_newsletter-checkbox"
							id={`id_${list.id}`}
							name="addToList"
							type="checkbox"
							defaultChecked={true}
							style={{ display: "none" }}
						/>
						<div className="NewsletterSignupForm__Flex">
							<label
								htmlFor={`id_${list.id}`}
								style={{
									position: "absolute",
									width: "1px",
									height: "1px",
									padding: 0,
									margin: "-1px",
									overflow: "hidden",
									clip: "rect(0,0,0,0)",
									border: 0,
								}}
							>
								Email Address
							</label>
							<input
								type="email"
								id="newEmail"
								required
								placeholder="Email Address"
							/>
							<div
								className={`NewsletterSignupForm__Button --min-w ${this.buttonClasses()}`}
								onClick={this.handleEmail}
							>
								Subscribe
							</div>
						</div>
					</div>
				)}

				{confirmAcctCreation && (
					<div className="NewsletterSignupForm__Confirmation">
						<p><b>Check your inbox!</b></p>
						<p>{this.state.confirmation.message}</p>
						<p>
							Don’t see an email? Please check your spam folder or{" "}
							<a href="https://talkingpointsmemo.com/contact">
								contact&nbsp;us
							</a>{" "}
							for assistance.
						</p>
					</div>
				)}

				{this.state.error && (
					<p
						style={{
							background: "#ffefb0",
							padding: "10px",
						}}
					>
						{this.state.error.message}
					</p>
				)}
			</div>
		);
	}

	renderContent() {
		if (this.state.error && !this.state.emailLists) return null;
		if (!this.state.emailLists) return null;

		const list = this.categoryToList(this.props.category);
		if (!list) return null;

		return this.renderForm(list);
	}
}